import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import {
  buildCampaignBillingRequiredActions,
  type CampaignBillingRequiredActionRow,
} from '../lib/marketing/campaignBillingRequiredActions';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';

const campaignsCache = createBusinessCache<CampaignBillingRequiredActionRow[]>({
  persistKey: 'campaign-billing',
});

/** Failed email-campaign billing (or unpaid send failures) needing retry. */
export function useCampaignBillingRequiredActions(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [campaigns, setCampaigns] = useState<CampaignBillingRequiredActionRow[]>(
    () => campaignsCache.get(businessId) ?? [],
  );
  const [isLoading, setIsLoading] = useState(() => !campaignsCache.has(businessId));
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = useCallback(async () => {
    if (!businessId || !supabase) return;

    const { data, error: fetchError } = await supabase
      .from('email_campaigns')
      .select(
        'id, name, status, billing_status, billing_payment_intent_id, recipient_count, scheduled_at, updated_at, created_at, channel_type',
      )
      .eq('business_id', businessId)
      .or('billing_status.eq.failed,status.eq.failed')
      .order('updated_at', { ascending: false })
      .limit(50);

    if (fetchError) throw fetchError;

    const mapped = ((data ?? []) as CampaignBillingRequiredActionRow[]).filter(
      (c) => (c.channel_type || 'email') !== 'sms',
    );
    setCampaigns(mapped);
    campaignsCache.set(businessId, mapped);
  }, [businessId, supabase]);

  const load = useCallback(async () => {
    if (!enabled || !businessId || !supabase) {
      setCampaigns([]);
      setIsLoading(false);
      return;
    }

    const hasCache = campaignsCache.has(businessId);
    if (hasCache) {
      setCampaigns(campaignsCache.get(businessId) ?? []);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      await fetchCampaigns();
    } catch (err) {
      console.error('Error loading campaign billing required actions:', err);
      setError('Failed to load campaign billing actions.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, supabase, fetchCampaigns]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setCampaigns([]);
      return;
    }
    void load();
  }, [load, enabled]);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase || isLoading) return;

    const channel = supabase
      .channel(`campaign-billing-required-actions-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'email_campaigns',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
          refreshDebounceRef.current = setTimeout(() => {
            void fetchCampaigns();
          }, 400);
        },
      )
      .subscribe();

    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, isLoading, fetchCampaigns]);

  const campaignBillingActions = useMemo(
    () => buildCampaignBillingRequiredActions(campaigns),
    [campaigns],
  );

  return {
    campaigns,
    campaignBillingActions,
    isLoading,
    error,
    refresh: load,
  };
}
